import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedScrollLockService } from '../../global/workbench-global';
import { WorkbenchRuntimeError } from '../../shared/errors';
import { Keys } from '../../shared/keys';
import { monkeyPatch } from '../../utils/monkey-patch';
import { querySelectorAllAsArray } from '../../utils/query-selector-all-as-array';
import { Size, ModalVariant } from './modal.types';
import { getRandomId } from '../../utils/get-random-id';

const FALLBACK_REMOVE_TIMEOUT_MS = 250;

const MODAL_BACK_BUTTON_SELECTOR = 'back-button';
const MODAL_CLOSE_BUTTON_SELECTOR = 'close-button';

const IS_ANIMATING = 'is-animating';

const SIZES: Record<Size, any> = {
  s: {
    'start-col-mq4': '5',
    mq1: '12',
    mq4: '4',
  },
  m: {
    'start-col-mq4': '3',
    'start-col-mq5': '4',
    mq1: '12',
    mq4: '8',
    mq5: '6',
  },
  l: {
    'start-col-mq5': '2',
    mq1: '12',
    mq5: '10',
  },
};

/**
 * @slot - one or multiple wb-modal-level elements
 */
@Component({
  tag: 'wb-modal',
  styleUrl: './modal.scss',
  shadow: true,
})
export class ModalComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** Modal variant to display */
  @Prop({ reflect: true })
  public variant!: ModalVariant;

  /** Modal size */
  @Prop({ reflect: true })
  public size: Size = 'm';

  /** If using multiple levels, this prop indicates which level to display (starting at 1) */
  @Prop({ reflect: true, mutable: true })
  public activeLevel: number = 1;

  /** If present, emits wbclose event on backdrop click */
  @Prop()
  public closeOnBackdropClick?: boolean = false;

  /** If present, emits wbclose event on escape key */
  @Prop()
  public closeOnEscapeKey?: boolean = false;

  /** If present, locks scrolling of the page */
  @Prop()
  public scrollLock?: boolean = false;

  /** Event emitted when modal wants to be open */
  @Event()
  public wbmodalopen!: EventEmitter<null>;

  /** Event emitted when modal wants to be closed */
  @Event()
  public wbmodalclose!: EventEmitter<null>;

  /**
   * @deprecated event will be removed
   * Event emitted when modal wants to be closed
   */
  @Event()
  public wbclose!: EventEmitter<null>;

  /** Event emitted when `activeLevel` changes */
  @Event()
  public wbmodallevelchange!: EventEmitter<number>;

  /**
   * @deprecated event will be removed
   * Event emitted when `activeLevel` changes
   */
  @Event()
  public wblevelchange!: EventEmitter<number>;

  /** Event delegation on click for close and back buttons */
  @Listen('click', { capture: true })
  public onModalClickEventCapture(event: MouseEvent): void {
    const path = event.composedPath();
    const isCloseButtonClick = path.find((el: HTMLElement) => el.classList?.contains(MODAL_CLOSE_BUTTON_SELECTOR));

    if (isCloseButtonClick) {
      this.triggerModalClose(event);
      return;
    }

    const isBackButtonClick = path.find((el: HTMLElement) => el.classList?.contains(MODAL_BACK_BUTTON_SELECTOR));

    if (isBackButtonClick) {
      this.onBackButtonClick();
    }
  }

  /** Setting capture to true prevents running this function when creating the modal as a result of a click event */
  @Listen('mousedown', { capture: true })
  public registerBackdropMouseDown(event: MouseEvent): void {
    if (this.closeOnBackdropClick === false) {
      return;
    }

    const isTargetOutsideOfDialog = !event.composedPath().includes(this.dialogEl);

    this.closeOnMouseUpOutsideOfDialog = isTargetOutsideOfDialog;
  }

  @Listen('mouseup')
  public triggerCloseOnBackdropMouseUpAndDown(event: MouseEvent): void {
    if (this.closeOnBackdropClick === false || this.closeOnMouseUpOutsideOfDialog === false) {
      return;
    }

    const isTargetOutsideOfDialog = this.dialogEl.contains(event.target as HTMLElement) === false;

    if (isTargetOutsideOfDialog) {
      this.closeOnMouseUpOutsideOfDialog = false;
      this.triggerModalClose(event, false);
    }
  }

  @Listen('keyup', { target: 'window' })
  public onKeyUp(event: KeyboardEvent): void {
    if (this.closeOnEscapeKey && event.key === Keys.ESC) {
      this.triggerModalClose(event);
    }
  }

  /** Emits a wbclose event when close modal action is triggered. */
  @Method()
  public async triggerModalClose(event: MouseEvent | KeyboardEvent, preventDefault: boolean = true): Promise<void> {
    if (preventDefault) {
      event.preventDefault();
    }

    this.wbclose.emit();
    this.wbmodalclose.emit();
  }

  /**
   * Runs the modal's leave animation on demand. This method should only be used in rare cases:
   * e.g. removing the modal when using React.
   */
  @Method()
  public async runLeaveAnimation(): Promise<void> {
    return new Promise((resolve) => {
      const onTransitionEnd = (): void => {
        this.el.removeEventListener('transitionend', onTransitionEnd);
        resolve();
      };

      this.el.addEventListener('transitionend', onTransitionEnd);
      this.el.removeAttribute('visible');
      setTimeout(onTransitionEnd, FALLBACK_REMOVE_TIMEOUT_MS); // fallback for missing transitionend event
    });
  }

  @Watch('activeLevel')
  public async onActiveLevelChange(newLevel: number, previousLevel: number): Promise<void> {
    if (this.el.hasAttribute('animating')) {
      throw new WorkbenchRuntimeError('activeLevel must not be updated during animation', 'wb-modal');
    }

    const previousLevelEl = this.el.querySelector<HTMLWbModalLevelElement>(`${this.TagNames.WbModalLevel}[level="${previousLevel}"]`);
    const nextLevelEl = this.el.querySelector<HTMLWbModalLevelElement>(`${this.TagNames.WbModalLevel}[level="${newLevel}"]`);

    // Prevent pointer-events
    this.el.classList.add(IS_ANIMATING);

    // Fade out content and hide footer
    await previousLevelEl.prepareToLeave();

    // Position the next level above the previous one
    await nextLevelEl.prepareToEnter();

    // Adjust the height of the next level
    if (this.activeLevel !== 1) {
      nextLevelEl.expandToFullHeight();
    }

    // Change the height of the previous level to the height of the next level
    const height = await nextLevelEl.getInnerHeight();
    await previousLevelEl.expandToHeight(height);

    // Remove previous level, clean up classes set during animations
    await previousLevelEl.leave();

    // Finish animation by setting active class
    await nextLevelEl.enter();

    // Allow pointer events
    this.el.classList.remove(IS_ANIMATING);

    // Emit wblevelchange
    if (newLevel !== previousLevel) {
      this.wblevelchange.emit(newLevel);
      this.wbmodallevelchange.emit(newLevel);
    }
  }

  private TagNames = transformTagNames(this, 'wb-modal', ['wb-grid', 'wb-grid-row', 'wb-grid-col', 'wb-modal-level']);
  private closeOnMouseUpOutsideOfDialog: boolean = false;
  private dialogEl!: HTMLElement;
  private levels: Array<HTMLWbModalLevelElement>;
  private uniqueId = getRandomId();

  public componentDidLoad(): void {
    this.setActiveLevel();
    this.addScrollLock();
    this.runEnterAnimation();
    this.prepareLeaveAnimation();
    this.wbmodalopen.emit();
  }

  public disconnectedCallback(): void {
    this.removeScrollLock();
  }

  private onBackButtonClick(): void {
    const newLevel = Math.max(1, this.activeLevel - 1);

    if (newLevel === this.activeLevel) {
      return;
    }

    this.activeLevel = newLevel;
  }

  private addScrollLock(): void {
    if (this.scrollLock) {
      SharedScrollLockService.lock(this.uniqueId);
    }
  }

  private removeScrollLock(): void {
    if (this.scrollLock) {
      SharedScrollLockService.unlock(this.uniqueId);
    }
  }

  private prepareLeaveAnimation(): void {
    monkeyPatch(this.el, 'remove', (done) => this.runLeaveAnimation().then(done));
  }

  private runEnterAnimation(): void {
    requestAnimationFrame(() => this.el.setAttribute('visible', ''));
  }

  private bindDialogEl = (el: HTMLWbGridColElement): void => {
    this.dialogEl = el;
  };

  private setActiveLevel = (): void => {
    this.levels = querySelectorAllAsArray<HTMLWbModalLevelElement>(this.el, this.TagNames.WbModalLevel);
    this.levels.forEach((levelEl: HTMLWbModalLevelElement) => {
      // pass down relevant props
      levelEl.variant = this.variant;

      // show active level
      if (levelEl.level === this.activeLevel) {
        levelEl.show();
      }
    });
  };

  private handleSlotChange = (): void => {
    this.setActiveLevel();
  };

  public render(): JSX.Element {
    const { WbGrid, WbGridRow, WbGridCol } = this.TagNames;

    return (
      //* data-on="main" is set to override top data-on="contrast" as the component has no contrast version to handle child component tokens */
      <Host class="wb-modal" data-on="main">
        <WbGrid>
          <WbGridRow>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <WbGridCol role="dialog" class="outer" aria-modal="true" {...SIZES[this.size]} ref={this.bindDialogEl}>
              <div class="inner">
                <slot onSlotchange={this.handleSlotChange} />
              </div>
            </WbGridCol>
          </WbGridRow>
        </WbGrid>
      </Host>
    );
  }
}
